import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { postApi } from 'views/services/api';
import { toast } from 'react-toastify';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Stack,
  Alert,
  Collapse,
  CircularProgress,
  LinearProgress
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';
import { strengthColor, strengthIndicator } from 'utils/password-strength';

// icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ============================|| RESET PASSWORD FORM ||============================ //

const AuthResetPassword = ({ ...others }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { token } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [strength, setStrength] = useState(0);
  const [level, setLevel] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorStatus, setErrorStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const changePassword = (value) => {
    const temp = strengthIndicator(value);
    setStrength(temp);
    setLevel(strengthColor(temp));
  };

  useEffect(() => {
    changePassword('');
  }, []);

  return (
    <>
      <Collapse in={errorStatus}>
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          onClose={() => setErrorStatus(false)}
          sx={{
            mb: 3,
            borderRadius: '8px',
            backgroundColor: theme.palette.error.light + '20',
            color: theme.palette.error.dark,
            border: '1px solid',
            borderColor: theme.palette.error.light + '50'
          }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

      <Typography variant="body2" sx={{ color: theme.palette.grey[600], mb: 3, lineHeight: 1.6 }}>
        Ingresa tu nueva contraseña. Asegúrate de elegir una contraseña segura.
      </Typography>

      <Formik
        initialValues={{
          password: '',
          confirmPassword: ''
        }}
        validationSchema={Yup.object().shape({
          password: Yup.string()
            .min(6, 'La contraseña debe tener al menos 6 caracteres')
            .max(255)
            .required('La contraseña es requerida'),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden')
            .required('La confirmación es requerida')
        })}
        onSubmit={async (values) => {
          setIsSubmitting(true);
          setErrorStatus(false);
          try {
            const response = await postApi('api/hotel/reset-password', {
              token,
              password: values.password
            });
            if (response.status === 200) {
              toast.success('¡Contraseña actualizada exitosamente!', {
                icon: <CheckCircleOutlineIcon />,
                theme: 'colored'
              });
              setTimeout(() => {
                navigate('/login');
              }, 1500);
            } else {
              setErrorStatus(true);
              setErrorMessage(response.response?.data?.message || 'El enlace de recuperación es inválido o ha expirado');
            }
          } catch (err) {
            setErrorStatus(true);
            setErrorMessage(err.response?.data?.message || 'Error de conexión con el servidor');
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <Stack spacing={2.5}>
              {/* New Password */}
              <FormControl fullWidth error={Boolean(touched.password && errors.password)}>
                <InputLabel htmlFor="reset-password-new">Nueva Contraseña</InputLabel>
                <OutlinedInput
                  id="reset-password-new"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  name="password"
                  onBlur={handleBlur}
                  onChange={(e) => {
                    handleChange(e);
                    changePassword(e.target.value);
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="ver contraseña"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="large"
                        sx={{
                          color: theme.palette.grey[500],
                          '&:hover': { backgroundColor: 'transparent', color: theme.palette.primary.main }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Nueva Contraseña"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[300] },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.light }
                  }}
                />
                {touched.password && errors.password && (
                  <FormHelperText error>{errors.password}</FormHelperText>
                )}
              </FormControl>

              {/* Strength Indicator */}
              {strength !== 0 && (
                <FormControl fullWidth sx={{ mt: -1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Box
                          style={{ backgroundColor: level?.color }}
                          sx={{ width: 85, height: 8, borderRadius: '7px' }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="subtitle1" fontSize="0.75rem">
                          {level?.label}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </FormControl>
              )}

              {/* Confirm Password */}
              <FormControl fullWidth error={Boolean(touched.confirmPassword && errors.confirmPassword)}>
                <InputLabel htmlFor="reset-password-confirm">Confirmar Contraseña</InputLabel>
                <OutlinedInput
                  id="reset-password-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={values.confirmPassword}
                  name="confirmPassword"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="ver contraseña"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="large"
                        sx={{
                          color: theme.palette.grey[500],
                          '&:hover': { backgroundColor: 'transparent', color: theme.palette.primary.main }
                        }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirmar Contraseña"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[300] },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.light }
                  }}
                />
                {touched.confirmPassword && errors.confirmPassword && (
                  <FormHelperText error>{errors.confirmPassword}</FormHelperText>
                )}
              </FormControl>

              {isSubmitting && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
                </Box>
              )}

              <Box>
                <AnimateButton>
                  <Button
                    disableElevation
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      padding: '12px 0',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                        <span>Actualizando...</span>
                      </Stack>
                    ) : (
                      'Restablecer Contraseña'
                    )}
                  </Button>
                </AnimateButton>
              </Box>
            </Stack>
          </form>
        )}
      </Formik>
    </>
  );
};

export default AuthResetPassword;
